import { autoinject, newInstance, computedFrom } from 'aurelia-framework';
import { applicationLogger } from "../../../logging/logger";
import { AureliaApplicationSettings } from "../../../models/aurelia-application-settings";
import { TaskQueue } from 'aurelia-task-queue';
import { Router } from "aurelia-router";
import { AdminApi } from "../../../services/admin-api";
import { Kiosk, KioskTypeOption, KioskSetting, ProcessingService } from "../../../models/kiosk";
import { KioskTypes } from "../../../models/kiosk-types";
import { KioskSettingNames } from "../../../models/kiosk-setting-names"; 
import { KioskSettingTypes } from "../../../models/kiosk-setting-types";
import { LogDates } from "../../../models/log-dates";
import { MagensaSettings } from "../../../models/magensa-settings";
import { MaterializeFormValidationRenderer, MdToastService, MdModal } from "aurelia-materialize-bridge";
import { TenantUser } from "../../../models/user";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { PasswordGenerator } from "../../../utility/password-generator";
import { Page } from "../../common/abstract-page";
import { OpenIdConnect } from "aurelia-open-id-connect";
import { TenantService } from "../../../services/tenant-service";
import * as moment from "moment";
import { KioskMeasurementChart } from './charts/kiosk-measurement-chart';
import { TicketStockChart } from './charts/ticket-stock-chart';
import { isInteger } from 'aurelia-i18n';

@autoinject
export class Kiosks extends Page {
    tenantId: string;
    tenantName: string;

    kiosks: Array<Kiosk>;
    newKiosk: Kiosk;
    selectedKiosk: Kiosk;
    magensaSettings: MagensaSettings;
    logDates: LogDates;
    showTransactionLogs: boolean;
    showHeadedKioskLogs: boolean;
    showCloudSyncLogs: boolean;
    clientSecret: string;
    hasClientSecret: boolean;

    addKioskValidationRules: ValidationRules;
    updateKioskValidationRules: ValidationRules;
    paymentSettingsValidationRules: ValidationRules;
    transactionAmountValidationRules: ValidationRules;
    passCodeValidationRules: ValidationRules;

    addKioskModal: MdModal;
    kioskSettingsModal: MdModal;
    kioskLogsModal: MdModal;
    paymentSettingsModal: MdModal;
    cloudSyncModal: MdModal;
    kioskMeasurementsModal: MdModal;
    ticketStockModal: MdModal;
    kioskTypes: Array<KioskTypeOption>;
    kioskFilters = [
        { value: '', keys: ['name', 'ssid'] }
    ];
    timeZones: Array<string>;

    processingServices: Array<ProcessingService>;

    chartViewModel: KioskMeasurementChart;

    ticketStockChartViewModel: TicketStockChart;

    constructor(adminApi: AdminApi,
        private applicationSettings: AureliaApplicationSettings,
        toast: MdToastService,
        router: Router,
        openIdConnect: OpenIdConnect,
        private tenantService: TenantService,
        @newInstance(ValidationController)
        private kioskValidationController: ValidationController,
        private taskQueue: TaskQueue,
        chartViewModel: KioskMeasurementChart,
        ticketStockChartViewModel: TicketStockChart) {

        super(adminApi, toast, router, openIdConnect);

        this.kioskTypes = new Array<KioskTypeOption>();
        this.chartViewModel = chartViewModel;
        this.ticketStockChartViewModel = ticketStockChartViewModel;
        for(const index in applicationSettings.KioskTypes) {
            const option = new KioskTypeOption(applicationSettings.KioskTypes[index]);
            this.kioskTypes.push(option);
        }

        this.processingServices = new Array<ProcessingService>();
        this.processingServices.push(new ProcessingService("MppGv3", "MppGv3TransactionProcessingService"));
        this.processingServices.push(new ProcessingService("Unigate", "UnigateTransactionProcessingService"));

        this.setupValidation();
    }

    async activate(params: any) {
        this.tenantId = params.tenantId;
        this.tenantName = this.tenantService.tenantName;
        this.clientSecret = null;
        this.hasClientSecret = false;

        const response = await this.adminApi.getTenantManagementData(this.tenantId);
        if (response.success && response.data) {
            this.kiosks = response.data.kiosks;
            this.timeZones = response.data.timeZones;
        } else {
            applicationLogger.error(response.message);
        }


    }


    isNumeric = (value: string): boolean => !isNaN(Number(value));

    setupValidation() {


        ValidationRules.customRule(
            'isNumber',
            (value,object) => value === null || value === undefined
                || this.isNumeric(value),
            `\${$displayName} must be a number.`
        );


        this.kioskValidationController.addRenderer(new MaterializeFormValidationRenderer());
        this.addKioskValidationRules = ValidationRules
            .ensure("name")
            .required()
            .ensure("ssid")
            .required()
            .ensure('type')
            .required().minLength(1)
            .ensure('reminderDays')
            .satisfiesRule('isNumber')
            .ensure('settings')
            .satisfies(settingsArray => {
                let passCodeValid = true;
                let transactionAmountValid = true;
                let defaultTicketStockAmountValid = true;
                if (this.newKiosk.type === KioskTypes.DumpStation) {
                    const passCodeIndex = settingsArray.findIndex(setting => setting.name ===  KioskSettingNames.PassCode);
                    if (passCodeIndex > -1) {
                        const passCode = settingsArray[passCodeIndex];
                        passCodeValid = /^\d+$/.test(passCode.value);
                    }
                }
                if (this.newKiosk.type !== KioskTypes.HeadedKiosk) {
                    const transactionAmountIndex = settingsArray.findIndex(setting => setting.name === KioskSettingNames.TransactionAmount);
                    const transactionAmount = settingsArray[transactionAmountIndex];
                    transactionAmountValid = /^\d+(?:\.\d{0,2})$/.test(transactionAmount.value);
                }

                if (this.newKiosk.type !== KioskTypes.DumpStation) {
                    const defaultTicketStockAmountIndex = settingsArray.findIndex(setting => setting.name === KioskSettingNames.DefaultTicketStockAmount)
                    if (defaultTicketStockAmountIndex > -1) {
                        const defaultTicketStockAmount = settingsArray[defaultTicketStockAmountIndex]
                        if (defaultTicketStockAmount.value)  {
                            defaultTicketStockAmountValid = /^\d+$/.test(defaultTicketStockAmount.value)
                        }
                    }
                }
                return passCodeValid && transactionAmountValid && defaultTicketStockAmountValid;
            })
            .on(Kiosk)
            .rules;

        this.updateKioskValidationRules = ValidationRules
            .ensure("name")
            .required()
            .ensure("ssid")
            .required()
            .ensure('type')
            .required().minLength(1)
            //.ensure('reminderDays')
            //.satisfiesRule('isNumber')
            .ensure('settings')
            .satisfies(settingsArray => {
                let passCodeValid = true;
                let transactionAmountValid = true;
                let defaultTicketStockAmountValid = true;
                if (this.selectedKiosk.type ===  KioskTypes.DumpStation) {
                    const passCodeIndex = settingsArray.findIndex(setting => setting.name === KioskSettingNames.PassCode);
                    if (passCodeIndex > -1) {
                        const passCode = settingsArray[passCodeIndex];
                        passCodeValid = /^\d+$/.test(passCode.value);

                    }
                }
                if (this.selectedKiosk.type !== KioskTypes.HeadedKiosk) {
                    const transactionAmountIndex = settingsArray.findIndex(setting => setting.name === KioskSettingNames.TransactionAmount);
                    const transactionAmount = settingsArray[transactionAmountIndex];
                    transactionAmountValid = /^\d+(?:\.\d{0,2})$/.test(transactionAmount.value);
                }

                if (this.selectedKiosk.type !== KioskTypes.DumpStation) {
                    const defaultTicketStockAmountIndex = settingsArray.findIndex(setting => setting.name === KioskSettingNames.DefaultTicketStockAmount);
                    if (defaultTicketStockAmountIndex > -1) {
                        const defaultTicketStockAmount = settingsArray[defaultTicketStockAmountIndex]
                        if (defaultTicketStockAmount.value) {
                            defaultTicketStockAmountValid = /^\d+$/.test(defaultTicketStockAmount.value)
                        }
                    }
                }
                return passCodeValid && transactionAmountValid && defaultTicketStockAmountValid;
            })
            .on(Kiosk)
            .rules;

        this.paymentSettingsValidationRules = ValidationRules
            .ensure("url")
            .required()
            .ensure("processorName")
            .required()
            .ensure('customerName')
            .required()
            .ensure('customerCode')
            .required()
            .ensure('username')
            .required()
            .ensure('password')
            .required()
            .ensure('merchantProcessor')
            .required()
            .on(MagensaSettings)
            .rules;

        this.transactionAmountValidationRules = ValidationRules
            .ensure((setting: KioskSetting) => setting.value)
            .required()
            .satisfies((transactionAmount) => {
                return /^\d+(?:\.\d{0,2})$/.test(transactionAmount);
            })
            .rules;

        this.passCodeValidationRules = ValidationRules
            .ensure((setting: KioskSetting) => setting.value)
            .required()
            .satisfies((passCode) => {
                return /^\d+$/.test(passCode);
            })
            .rules;
    }

    notifyCopy(kioskId: string) {
        this.toast.show(`Copied kiosk id: '${kioskId}'`);
    }

    async addKiosk(e) {
        this.kioskValidationController.validate({ object: this.newKiosk, rules: this.addKioskValidationRules }).then(async v => {
            if (v.valid) {
                const response = await this.adminApi.addKiosk(this.newKiosk);
                if (response.success) {
                    this.toast.show(response.message);
                    this.kiosks.push(response.data);
                    this.addKioskModal.close();
                } else {
                    this.toast.show(response.message, 5000, "red");
                }
            }
        });
    }

    async cancelAddKiosk() {
        this.addKioskModal.close();
    }

    manageNewDumpStationSettings() {
        this.newKiosk.settings = new Array<KioskSetting>();
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.TransactionAmount, KioskSettingTypes.General));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.PassCode, KioskSettingTypes.General));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.UploadLogFiles, KioskSettingTypes.Boolean));
       
        this.addNewKioskHealthSettings();
        
    }

    manageNewHeadedKioskSettings() {
        this.newKiosk.settings = new Array<KioskSetting>();
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.Workflow, KioskSettingTypes.Workflow));
        //this.newKiosk.settings.push(new KioskSetting("DayPassTicketTemplate", "Ticket"));
        //this.newKiosk.settings.push(new KioskSetting("CampsiteTicketTemplate", "Ticket"));
        //this.newKiosk.settings.push(new KioskSetting("ExtraVehiclesTicketTemplate", "Ticket"));
        //this.newKiosk.settings.push(new KioskSetting("FirewoodTicketTemplate", "Ticket"));
        //this.newKiosk.settings.push(new KioskSetting("HikerBikerTicketTemplate", "Ticket"));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.UploadLogFiles, KioskSettingTypes.Boolean));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.ResetTicketStockAmount, KioskSettingTypes.Boolean));

        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.DefaultTicketStockAmount, KioskSettingTypes.Integer));

        this.addNewKioskHealthSettings();
    
    }

    manageNewKioskSettings() {
        this.newKiosk.settings = new Array<KioskSetting>();
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.TransactionAmount, KioskSettingTypes.General));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.TicketTemplate, KioskSettingTypes.Ticket));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.UploadLogFiles, KioskSettingTypes.Boolean));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.ResetTicketStockAmount, KioskSettingTypes.Boolean));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.DefaultTicketStockAmount, KioskSettingTypes.Integer));

        this.addNewKioskHealthSettings();
    }

    addNewKioskHealthSettings() {
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.SendHealthEmail, KioskSettingTypes.Health));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.SendDelayedKioskCloudSyncEmail, KioskSettingTypes.Health));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.SendLowBatteryVoltageEmail, KioskSettingTypes.Health));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.SendLowTicketStockEmail, KioskSettingTypes.Health));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.DaysSinceLastSyncThreshold, KioskSettingTypes.Health));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.LowTicketStockThreshold, KioskSettingTypes.Health));
        this.newKiosk.settings.push(new KioskSetting(KioskSettingNames.LowBatteryVoltageThreshold, KioskSettingTypes.Health));
    }

    kioskTypeChanged() {

        switch (this.newKiosk.type) {
            case "DumpStation":
                this.manageNewDumpStationSettings();
                break;
            case "HeadedKiosk":
                this.manageNewHeadedKioskSettings();
                break;
            default:
                this.manageNewKioskSettings();
                break;
        }
    }

    async openAddKioskModal() {
        this.newKiosk = new Kiosk();
        const setting = new KioskSetting(KioskSettingNames.TransactionAmount, KioskSettingTypes.General);
        this.newKiosk.settings.push(setting);
        this.newKiosk.tenantId = this.tenantId;
        this.newKiosk.timeZone = "Pacific Standard Time";
        this.addKioskModal.open();
    }

    manageTransactions(kioskId: string) {
        this.router.navigateToRoute("transactions", { tenantId: this.tenantId, kioskId: kioskId });
    }

    async updateKiosk(e) {
        this.kioskValidationController.validate({ object: this.selectedKiosk, rules: this.updateKioskValidationRules }).then(async v => {
            if (v.valid) {
                const response = await this.adminApi.updateKiosk(this.selectedKiosk);
                if (response.success) {
                    this.toast.show(response.message);
                    const index = this.kiosks.indexOf(this.selectedKiosk);
                    this.kiosks[index] = response.data;
                    this.selectedKiosk = response.data;
                    this.kioskSettingsModal.close();

                } else {
                    this.toast.show(response.message, 5000, "red");
                }
            }
        });
    }

    manageDumpStationSettings() {
        const passCodeIndex = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.PassCode) as any);
        if (passCodeIndex === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.PassCode, KioskSettingTypes.General));
        }

        let index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.UploadLogFiles) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.UploadLogFiles, KioskSettingTypes.Boolean));
        }

        //const ticketStockIndex = this.selectedKiosk.settings.findIndex((setting => setting.name === "DefaultTicketStockAmount") as any);
        //if (ticketStockIndex === -1) {
        //    this.selectedKiosk.settings.push(new KioskSetting("DefaultTicketStockAmount", "Integer"));
        //}
        this.manageKioskHealthSettings();

       
    }

    manageKioskHealthSettings() {
        let index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.SendHealthEmail) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.SendHealthEmail, KioskSettingTypes.Health));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.SendDelayedKioskCloudSyncEmail) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.SendDelayedKioskCloudSyncEmail, KioskSettingTypes.Health));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.SendDeclinedBatchTransactionEmail) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.SendDeclinedBatchTransactionEmail, KioskSettingTypes.Health));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.SendLowBatteryVoltageEmail) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.SendLowBatteryVoltageEmail, KioskSettingTypes.Health));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.SendLowTicketStockEmail) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.SendLowTicketStockEmail, KioskSettingTypes.Health));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.DaysSinceLastSyncThreshold) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.DaysSinceLastSyncThreshold, KioskSettingTypes.Health));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.LowTicketStockThreshold) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.LowTicketStockThreshold, KioskSettingTypes.Health));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.LowBatteryVoltageThreshold) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.LowBatteryVoltageThreshold, KioskSettingTypes.Health));
        }

    }

    manageHeadedKioskSettings() {

        let index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.Workflow) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.Workflow, KioskSettingTypes.Workflow));
        }


        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.UploadLogFiles) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.UploadLogFiles, KioskSettingTypes.Boolean));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.ResetTicketStockAmount) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.ResetTicketStockAmount, KioskSettingTypes.Boolean));
        }

        const ticketStockIndex = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.DefaultTicketStockAmount) as any);
        if (ticketStockIndex === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.DefaultTicketStockAmount, KioskSettingTypes.Integer));
        }

        this.manageKioskHealthSettings();
     
    }

    getTicketTemplateNames() {
       
       // this.selectedKiosk = (this.kiosks.find((kiosk): boolean => kiosk.id === kioskId)) as Kiosk;
        var ticketTemplateNames = this.getTicketTemplatesFromWorkflow();

        for (var ticketTemplateName of ticketTemplateNames) {
            var index = this.selectedKiosk.settings.findIndex((setting => setting.name === ticketTemplateName) as any);
            if (index === -1) {
                this.selectedKiosk.settings.push(new KioskSetting(ticketTemplateName, KioskSettingTypes.Ticket));
            }
        }
    }

    processWorkflows(purchaseWorkflows: Array<any>): string[] {
        const templateSet = new Set<string>();

        for (const purchaseWorkflow of purchaseWorkflows) {
            for (const step of purchaseWorkflow.WorkflowSteps ?? []) {
                if (step.TicketTemplateName) {
                    templateSet.add(step.TicketTemplateName);
                }

                for (const item of step.PurchaseItems ?? []) {
                    if (item.TicketTemplateName) {
                        templateSet.add(item.TicketTemplateName);
                    }
                }
            }
        }

        return Array.from(templateSet);
    }

    getTicketTemplatesFromWorkflow(): string[] {
        const setting = this.selectedKiosk.settings.find(
            item => item.type === KioskSettingTypes.Workflow
        );

        if (!setting) return [];

        const workflow = JSON.parse(setting.value);

        if (workflow.PurchaseWorkflows) {
            return this.processWorkflows(workflow.PurchaseWorkflows);
        }

        if (Array.isArray(workflow.ParentWorkflows) && workflow.ParentWorkflows.length > 0) {
            const allTemplates = workflow.ParentWorkflows.flatMap(parent =>
                this.processWorkflows(parent.ChildWorkflows)
            );
            return Array.from(new Set(allTemplates));
        }


        return [];
    }

    manageKioskSettings() {
        let index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.TicketTemplate) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.TicketTemplate, KioskSettingTypes.Boolean));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.UploadLogFiles) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.UploadLogFiles, KioskSettingTypes.Boolean));
        }

        index = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.ResetTicketStockAmount) as any);
        if (index === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.ResetTicketStockAmount, KioskSettingTypes.Boolean));
        }

        const ticketStockIndex = this.selectedKiosk.settings.findIndex((setting => setting.name === KioskSettingNames.DefaultTicketStockAmount) as any);
        if (ticketStockIndex === -1) {
            this.selectedKiosk.settings.push(new KioskSetting(KioskSettingNames.DefaultTicketStockAmount, KioskSettingTypes.Integer));
        }

        this.manageKioskHealthSettings();
    }

   // @computedFrom(`selectedKiok`, 'selectedKiosk.settings')
    get uploadLogFiles(): boolean {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.UploadLogFiles) as KioskSetting;
            if (setting) {
                return setting.value === "True";
            }
        }
        return false;
    }

    set uploadLogFiles(val: boolean) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.UploadLogFiles) as KioskSetting;
        if (setting) {
            setting.value = val ? "True" : "False";
        }
    }

    get resetTicketStockAmount(): boolean {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.ResetTicketStockAmount) as KioskSetting;
            if (setting) {
                return setting.value === "True";
            }
        }
        return false;
    }

    set resetTicketStockAmount(val: boolean) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.ResetTicketStockAmount) as KioskSetting;
        if (setting) {
            setting.value = val ? "True" : "False";
        }
    }

    //@computedFrom(`selectedKiok`, 'selectedKiosk.settings') @computedFrom(`selectedKioks`)
    get sendHealthEmail(): boolean {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.SendHealthEmail) as KioskSetting;
            if (setting) {
                return setting.value === "True";
            }
        }
        return false;
    }

    set sendHealthEmail(val: boolean) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.SendHealthEmail) as KioskSetting;
        if (setting) {
            setting.value = val ? "True" : "False";
        }
    }


   // @computedFrom(`selectedKiok`, 'selectedKiosk.settings')
    get sendDelayedKioskCloudSyncEmail(): boolean {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.SendDelayedKioskCloudSyncEmail) as KioskSetting;
            if (setting) {
                return setting.value === "True";
            }
        }
        return false;
    }

    set sendDeclinedBatchTransactionEmail(val: boolean) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.SendDeclinedBatchTransactionEmail) as KioskSetting;
        if (setting) {
            setting.value = val ? "True" : "False";
        }
    }


    //@computedFrom(`selectedKiok`, 'selectedKiosk.settings')
    get sendDeclinedBatchTransactionEmail(): boolean {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.SendDeclinedBatchTransactionEmail) as KioskSetting;
            if (setting) {
                return setting.value === "True";
            }
        }
        return false;
    }

    set sendDelayedKioskCloudSyncEmail(val: boolean) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.SendDelayedKioskCloudSyncEmail) as KioskSetting;
        if (setting) {
            setting.value = val ? "True" : "False";
        }
    }


    //@computedFrom(`selectedKiosk`, 'selectedKiosk.settings')
    get sendLowBatteryVoltageEmail(): boolean {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.SendLowBatteryVoltageEmail) as KioskSetting;
            if (setting) {
                return setting.value === "True";
            }
        }
        return false;
    }

    set sendLowBatteryVoltageEmail(val: boolean) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.SendLowBatteryVoltageEmail) as KioskSetting;
        if (setting) {
            setting.value = val ? "True" : "False";
        }
    }


    //@computedFrom(`selectedKiosk`, 'selectedKiosk.settings')
    get sendLowTicketStockEmail(): boolean {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.SendLowTicketStockEmail) as KioskSetting;
            if (setting) {
                return setting.value === "True";
            }
        }
        return false;
    }

    set sendLowTicketStockEmail(val: boolean) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.SendLowTicketStockEmail) as KioskSetting;
        if (setting) {
            setting.value = val ? "True" : "False";
        }
    }


    //@computedFrom(`selectedKiosk`, 'selectedKiosk.settings')
    get daysSinceLastSyncThreshold(): number {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.DaysSinceLastSyncThreshold) as KioskSetting;
            if (setting) {
                var days = parseFloat(setting.value);
                return Number.isNaN(days) ? null : days;
            }
        }
        return null;
    }

    set daysSinceLastSyncThreshold(val: number) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.DaysSinceLastSyncThreshold) as KioskSetting;
        if (setting) {
            setting.value = val.toString();
        }
    }


    //@computedFrom(`selectedKiosk`, 'selectedKiosk.settings')
    get lowTicketStockThreshold(): number {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.LowTicketStockThreshold) as KioskSetting;
            if (setting) {
                var ticketStock = parseInt(setting.value);
                return Number.isNaN(ticketStock) ? null : ticketStock;
            }
        }
        return null;
    }

    set lowTicketStockThreshold(val: number) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.LowTicketStockThreshold) as KioskSetting;
        if (setting) {
            setting.value = val.toString();
        }
    }


    //@computedFrom(`selectedKiosk`, 'selectedKiosk.settings')
    get lowBatteryVoltageThreshold(): number {
        if (this.selectedKiosk && this.selectedKiosk.settings) {
            const setting =
                this.selectedKiosk.settings.find(s => s.name === KioskSettingNames.LowBatteryVoltageThreshold) as KioskSetting;
            if (setting) {
                var voltage = parseFloat(setting.value);
                return Number.isNaN(voltage) ? null : voltage;
            }
        }
        return null;
    }

    set lowBatteryVoltageThreshold(val: number) {
        const setting = this.selectedKiosk.settings.find(setting => setting.name === KioskSettingNames.LowBatteryVoltageThreshold) as KioskSetting;
        if (setting) {
            setting.value = val.toString();
        }
    }

    async openKioskSettingsModal(kioskId: string) {
        this.selectedKiosk = (this.kiosks.find((kiosk): boolean => kiosk.id === kioskId)) as Kiosk;

        var result = await this.adminApi.getKioskSettings(kioskId);

        if (result.success) {
            this.selectedKiosk.settings = result.data;
        }

        this.clientSecret = null;
        this.hasClientSecret = false;

        switch (this.selectedKiosk.type) {
        case "DumpStation":
            this.manageDumpStationSettings();
            break;
        case "HeadedKiosk":
            this.manageHeadedKioskSettings();
            break;
        default:
            this.manageKioskSettings();
            break;
        }

        this.kioskSettingsModal.open();

        this.taskQueue.queueMicroTask(() => {
            for (var setting of this.selectedKiosk.settings.filter(s => s.type === KioskSettingTypes.Ticket)) {
                var value = setting.value;
                setting.value = null;
                setting.value = value;
            }
        })
       
    }

    async cancelUpdateKiosk() {
        this.kioskSettingsModal.close();
    }

    async openKioskLogsModal(kioskId: string) {
        this.selectedKiosk = (this.kiosks.find((kiosk): boolean => kiosk.id === kioskId)) as Kiosk;
        this.showHeadedKioskLogs = false;
        this.showTransactionLogs = false;
        this.showCloudSyncLogs = false;
        const response = await this.adminApi.getKioskLogDates(kioskId, this.selectedKiosk.tenantId);
        if (response.success) {
            this.logDates = response.data;
            this.showHeadedKioskLogs = (this.logDates && this.logDates.headedkiosk && (this.logDates.headedkiosk.length > 0));
            this.showTransactionLogs = (this.logDates && this.logDates.transaction && (this.logDates.transaction.length > 0));
            this.showCloudSyncLogs = (this.logDates && this.logDates.cloudsync && (this.logDates.cloudsync.length > 0));
            this.kioskLogsModal.open();
        } else {
            this.toast.show(response.message, 5000, "red");                     
        } 
    }

    async downloadLogFile(date: Date, logType: string) {
        await this.adminApi.downloadKioskLog(this.selectedKiosk.id, this.selectedKiosk.name, logType, date, this.selectedKiosk.tenantId);
    }

    async downloadLogBundle(logType: string) {
        await this.adminApi.downloadKioskLogBundle(this.selectedKiosk.id, this.selectedKiosk.name, logType, this.selectedKiosk.tenantId);
    }

    async closeKioskLogsModal() {
        this.kioskValidationController.validate({ object: this.selectedKiosk, rules: this.updateKioskValidationRules }).then(async v => {
            if (v.valid) {
                const response = await this.adminApi.updateKiosk(this.selectedKiosk);
                if (response.success) {
                    this.toast.show(response.message);
                    const index = this.kiosks.indexOf(this.selectedKiosk);
                    this.kiosks[index] = response.data;
                    this.kioskLogsModal.close();
                } else {
                    this.toast.show(response.message, 5000, "red");
                }
            }
        });
    }

    async updatePaymentSettings(e) {
        this.kioskValidationController.validate({ object: this.magensaSettings, rules: this.paymentSettingsValidationRules }).then(async v => {
            if (v.valid) {
                const response = await this.adminApi.updatePaymentSettings(this.selectedKiosk.id, this.magensaSettings);
                if (response.success) {
                    this.toast.show(response.message);
                    this.paymentSettingsModal.close();
                } else {
                    this.toast.show(response.message, 5000, "red");
                }
            }
        });
    }

    async openPaymentSettingsModal(id: string) {
        const response = await this.adminApi.getPaymentSettings(this.selectedKiosk.id);
        if (response.success) {
            this.magensaSettings = response.data;
            this.paymentSettingsModal.open();
        } else {
            this.toast.show(response.message, 5000, "red");
        }
    }

    async openCloudSyncModal(id: string) {

        this.cloudSyncModal.open();
    }

    async closeCloudSyncModal() {
        this.cloudSyncModal.close();
    }

    async cancelCloudSyncSettings() {
        this.cloudSyncModal.close();
    }

    async cancelUpdatePaymentSettings() {
        this.paymentSettingsModal.close();
    }


    async openKioskMeasurementsModal(kiosk: Kiosk) {
        
        this.selectedKiosk = kiosk;
        this.chartViewModel.bindKiosk(kiosk);
        this.kioskMeasurementsModal.open();
       
    }

    async closeKioskMeasurementsModal() {
        this.kioskMeasurementsModal.close();
    }

    async cancelKioskMeasurementsModal() {
        this.cloudSyncModal.close();
    }

    async openTicketStockModal(kiosk: Kiosk) {

        this.selectedKiosk = kiosk;
        this.ticketStockChartViewModel.bindKiosk(kiosk);
        this.ticketStockModal.open();

    }

    async closeTicketStockModal() {
        this.ticketStockModal.close();
    }

    async cancelTicketStockModal() {
        this.ticketStockModal.close();
    }

    autoGeneratePassword() {
        this.newKiosk.password = PasswordGenerator.generatePassword(8);
    }

    async generateClientSecret() {
        const response = await this.adminApi.generateClientSecret(this.selectedKiosk.id);
        if (response.success) {
            this.toast.show(response.message);
            this.clientSecret = response.data;
            this.hasClientSecret = true;
            this.selectedKiosk.machineLogin = true;

        } else {
            this.toast.show(response.message, 5000, "red");
        }
    }
}